import { useState, useEffect } from 'react';
import {
  Box, Typography, TextField, Button, CircularProgress, Chip, Paper,
} from '@mui/material';
import { Save as SaveIcon, RestartAlt as ResetIcon } from '@mui/icons-material';
import api from '../../config/api';
import { useTranslation } from 'react-i18next';
import useStore from '../../config/store';
// Mismo ⓘ del wizard de vacantes y del ERP: un solo lenguaje visual
// para las ayudas de campo (ver src/erp/LabelWithInfo.js).
import { InfoTip } from '../../erp/LabelWithInfo';

/**
 * Mensajes de la ENTRADA LIBRE del bot de WhatsApp (F6 Paquete 3) — nivel
 * ORGANIZACIÓN: lo que responde el bot cuando alguien escribe al número sin
 * link de vacante. Vacío = default del sistema (mostrado como placeholder).
 * El backend valida variables desconocidas al guardar. Solo admin.
 */
// Grupos de mensajes (2026-09-07: región en vez de comuna, saludo recurrente y
// banco de talento). El nombre del bot (ej. "Sofía") va en estos textos.
const FIELD_GROUPS = [
  { key: 'busqueda', fields: ['saludoLibre', 'saludoLibreRecurrente', 'listaVacantes', 'listaVacantesFallback', 'sinVacantes'] },
  { key: 'bancoTalento', fields: ['pieBancoTalento', 'ofertaBancoTalento', 'bancoNombre', 'bancoNacionalidad', 'bancoDocumento', 'bancoEmail', 'bancoTelefono', 'bancoTipoTrabajo', 'bancoRegion', 'consentimientoBancoTalento', 'registroBancoTalentoOk', 'cierreBancoTalento'] },
];
const FIELD_KEYS = FIELD_GROUPS.flatMap((g) => g.fields);

const AtsWhatsappFreeTab = () => {
  const { t } = useTranslation();
  const tc = (k, o) => t(`ats.config.whatsappFree.${k}`, o);
  const { organization, authToken, showAlert } = useStore.getState();
  const authConfig = {
    params: { organizationId: organization },
    headers: { Authorization: `Bearer ${authToken}` },
  };

  const [messages, setMessages] = useState({});
  const [defaults, setDefaults] = useState({});
  const [variables, setVariables] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!organization) { setLoading(false); return; }
      try {
        setLoading(true);
        const res = await api.get('/organizations/captation-messages', authConfig);
        const data = res.data.response?.data || {};
        setMessages(data.messages || {});
        setDefaults(data.defaults || {});
        setVariables(data.variables || {});
      } catch (err) {
        console.error(err);
        showAlert(tc('loadError'), 'error');
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const body = {};
      FIELD_KEYS.forEach((k) => { body[k] = messages[k] || ''; });
      const res = await api.put('/organizations/captation-messages', body, authConfig);
      if (res.data.response?.status_response === 'OK') {
        setMessages(res.data.response.data?.messages || {});
        showAlert(tc('saved'), 'success');
      } else {
        showAlert(res.data.response?.message || tc('saveError'), 'error');
      }
    } catch (err) {
      console.error(err);
      showAlert(err.response?.data?.response?.message || tc('saveError'), 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>
        {tc('title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {tc('description')}
      </Typography>

      {FIELD_GROUPS.map((group) => (
      <Box key={group.key} sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
        <Box>
          <Typography variant="subtitle1" fontWeight={700}>{tc(`groups.${group.key}.title`)}</Typography>
          <Typography variant="caption" color="text.secondary">{tc(`groups.${group.key}.hint`)}</Typography>
        </Box>
        {group.fields.map((key) => (
          <Paper key={key} elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 2, p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" fontWeight={700}>{tc(`fields.${key}.label`)}</Typography>
                {/* El ⓘ aparece solo en los campos que tienen texto largo en i18n */}
                {tc(`fields.${key}.tooltip`, { defaultValue: '' })
                  ? <InfoTip title={tc(`fields.${key}.tooltip`)} />
                  : null}
              </Box>
              {messages[key] ? (
                <Button
                  size="small"
                  color="inherit"
                  startIcon={<ResetIcon fontSize="small" />}
                  onClick={() => setMessages((prev) => ({ ...prev, [key]: '' }))}
                  sx={{ fontSize: '0.7rem' }}
                >
                  {tc('restoreDefault')}
                </Button>
              ) : (
                <Chip label={tc('usingDefault')} size="small" sx={{ height: 20, fontSize: '0.65rem' }} />
              )}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              {tc(`fields.${key}.hint`)}
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={2}
              maxRows={6}
              value={messages[key] || ''}
              placeholder={defaults[key] || ''}
              onChange={(e) => setMessages((prev) => ({ ...prev, [key]: e.target.value }))}
              InputProps={{ sx: { fontSize: '0.85rem' } }}
            />
            <Box sx={{ display: 'flex', gap: 0.5, mt: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              <Typography variant="caption" color="text.secondary">{tc('variablesLabel')}:</Typography>
              {(variables[key] || []).map((v) => (
                <Chip key={v} label={`{{${v}}}`} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
              ))}
            </Box>
          </Paper>
        ))}
      </Box>
      ))}

      <Button
        variant="contained"
        startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
        disabled={saving}
        onClick={handleSave}
        sx={{ backgroundColor: '#6A5AF9', '&:hover': { backgroundColor: '#5B4DE8' } }}
      >
        {t('save')}
      </Button>
    </Box>
  );
};

export default AtsWhatsappFreeTab;
